import { useState } from "react";
import { ClipboardList, MessageCircle, Phone, UserRound } from "lucide-react";
import { Link } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile";

import { LogActivityForm } from "../activityLog/LogActivityForm";
import { DealProfileSheet } from "../dealProfile/DealProfileSheet";
import { StatusPill } from "../design/StatusPill";
import { LeadWhatsAppBadges } from "../whatsapp/LeadWhatsAppBadges";
import { WhatsAppAction } from "../whatsapp/WhatsAppAction";
import { buildWhatsAppGreeting } from "../whatsapp/whatsappMessage";
import { buildTelLink, buildWhatsAppLink, type HoyCard } from "./hoyPlan";
import type { LeadAlertCard } from "../alerts/useLeadAlerts";

/**
 * Almikia Epic EA (TASK-112): the "Quién te necesita hoy" person card
 * (design-system.md section 6) plus its actions, extracted out of
 * `AdvisorToday.tsx` (which was growing past its focused-file size by
 * appending here instead) so the card, its reason copy and its
 * "Registrar actividad" trigger are one cohesive, independently readable
 * unit. Renders identically on desktop and the 390 px mobile shell.
 */

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The card's "motivo" line -- names the activity for a catalogue next-step task, generic copy otherwise. */
function reasonText(translate: Translate, card: HoyCard): string {
  switch (card.reason.kind) {
    case "prospect":
      return translate("crm.dashboard.hoy.reason.prospect", {
        hours: card.reason.hours,
      });
    case "task_overdue":
      return card.reason.activityName
        ? translate("crm.dashboard.hoy.reason.next_step_overdue", {
            activity: card.reason.activityName,
          })
        : translate("crm.dashboard.hoy.reason.task_overdue");
    case "task_today":
      return card.reason.activityName
        ? translate("crm.dashboard.hoy.reason.next_step_today", {
            activity: card.reason.activityName,
          })
        : translate("crm.dashboard.hoy.reason.task_today");
    case "birthday":
      return translate(`crm.dashboard.hoy.reason.birthday_${card.reason.when}`);
    case "posi_incomplete":
      return translate("crm.dashboard.hoy.reason.posi_incomplete", {
        answered: card.reason.answered,
        field: translate(
          `crm.dashboard.hoy.posi_field.${card.reason.missingField}`,
        ),
      });
    default:
      return "";
  }
}

/** The card's navigation target: the lead show on desktop, its first contact on mobile (ASSUMPTION, see hoyPlan.ts). */
function cardHref(card: HoyCard, isMobile: boolean): string | undefined {
  if (!isMobile && card.dealId != null) return `/deals/${card.dealId}/show`;
  if (card.contactId != null) return `/contacts/${card.contactId}/show`;
  return undefined;
}

/** The first word of a display name, for the fallback WhatsApp greeting below. */
const firstWord = (name: string): string => name.trim().split(/\s+/)[0] ?? "";

const UrgencyChip = ({ hours }: { hours: number }) => (
  <span
    className="ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12.5px] font-semibold"
    style={{ backgroundColor: "#FBE7E4", color: "#B3321C" }}
  >
    ● {hours} h
  </span>
);

/**
 * A person card (design-system.md section 6). The name/motivo area and the
 * action buttons are separate focusable elements, deliberately NOT one
 * nested inside the other -- a `<Link>` wrapping `<a>`/`<button>` elements
 * would be invalid nested interactive content and would break keyboard tab
 * order (regression fixed in a previous attempt at this ticket).
 *
 * Almikia Epic ER (TASK-007, retry round): `alert` is this lead's own open
 * `lead_alerts` row, when `AdvisorToday.tsx` excluded it from
 * `LeadAlertCards` because it is already shown here (acceptance criterion
 * 2, "appears once (with its alert reason)") -- the red chip and the
 * alert's one-line reason render alongside the card's own "why" copy
 * instead of a second, duplicate card. `onAlertActivityLogged` re-evaluates
 * alerts after logging so a resolved one clears here too, without reload.
 */
export const HoyPersonCard = ({
  card,
  translate,
  alert,
  onAlertActivityLogged,
}: {
  card: HoyCard;
  translate: Translate;
  alert?: Pick<LeadAlertCard, "label" | "message"> | null;
  onAlertActivityLogged?: () => void;
}) => {
  const isMobile = useIsMobile();
  const href = cardHref(card, isMobile);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  // Every lead-backed card (Prospecto SLA, POSI incompleto, next-step task)
  // offers "Registrar actividad"; birthday and manual-task cards do not
  // (Almikia Epic EA, TASK-112 acceptance criteria).
  const canLogActivity = card.dealId != null;
  // Almikia Epic EF (TASK-122): the same lead-backed cards also offer
  // "Perfil del cliente" -- mobile has no lead screen (QEA11), so this is
  // the only mobile entry point to the TASK-119 section. Desktop is
  // unchanged: its Hoy card already links to the lead show, where the
  // section lives.
  const canOpenProfile = isMobile && card.dealId != null;

  const NameAndReason = (
    <div className="min-w-0 flex-1">
      <div className="truncate text-base font-bold">
        {card.name}
        {card.reason.kind === "prospect" ? (
          <UrgencyChip hours={card.reason.hours} />
        ) : null}
        {alert ? (
          <StatusPill tone="not-ok" text={alert.label} className="ml-2" />
        ) : null}
      </div>
      {/* design-system.md section 5: body copy >= 15px desktop / 16px
          mobile -- the card's one-line "motivo" is body copy, not a label. */}
      <div className="truncate text-base text-muted-foreground">
        {reasonText(translate, card)}
      </div>
      {alert ? (
        <div className="truncate text-base text-muted-foreground">
          {alert.message}
        </div>
      ) : null}
      {/* Almikia Epic EW2 (TASK-080): clock alerts, 'Responde' and unread count of the lead. */}
      <LeadWhatsAppBadges dealId={card.dealId} className="mt-1" />
    </div>
  );

  // Almikia Epic EF (TASK-122 retry round): at the 390 px mobile shell, the
  // extra "Perfil del cliente" button pushes the action row to 4 buttons
  // (WhatsApp, Call, "Registrar actividad", "Perfil del cliente" -- 4 x 44px
  // + gaps = 200px), which squeezes the name/reason column below what
  // `truncate` can keep readable. On mobile the actions move to their own
  // row under the name/reason; desktop keeps the original single-row layout.
  const actions =
    card.phone || canLogActivity || canOpenProfile ? (
      <div className="flex shrink-0 gap-2">
        {card.phone ? (
          <>
            {/* Almikia Epic EW1 (TASK-125): the shared "Enviar WhatsApp"
                action (TASK-124) replaces the old bare `wa.me` link -- it
                prefills the message per the shared precedence and logs the
                contact on the lead in the background. `card.contact` is
                normally set whenever `card.phone` is (hoyPlan.ts builds one
                off the card's own phone for every kind, birthdays included);
                the plain link below is a defensive fallback for the rare
                case it still isn't, so the button is never simply missing. */}
            {card.contact ? (
              <WhatsAppAction contact={card.contact} dealId={card.dealId} />
            ) : (
              <a
                href={buildWhatsAppLink(
                  card.phone,
                  buildWhatsAppGreeting({
                    nombre: firstWord(card.name),
                    asesora: null,
                  }),
                )}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={translate("crm.whatsapp.action_label", {
                  nombre: card.name,
                  _: `Enviar WhatsApp a ${card.name}`,
                })}
                className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F5EC] text-[#23794A]"
              >
                <MessageCircle aria-hidden="true" className="h-5 w-5" />
              </a>
            )}
            <a
              href={buildTelLink(card.phone)}
              aria-label={translate("crm.dashboard.hoy.action.call")}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FBF3D9] text-[#8A6D1D]"
            >
              <Phone aria-hidden="true" className="h-5 w-5" />
            </a>
          </>
        ) : null}
        {canLogActivity ? (
          <button
            type="button"
            onClick={() => setActivityLogOpen(true)}
            aria-label={translate("resources.deals.action.log_activity", {
              _: "Registrar actividad",
            })}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"
          >
            <ClipboardList aria-hidden="true" className="h-5 w-5" />
          </button>
        ) : null}
        {canOpenProfile ? (
          <button
            type="button"
            onClick={() => setProfileSheetOpen(true)}
            aria-label={translate("crm.dashboard.hoy.action.view_profile", {
              _: "Perfil del cliente",
            })}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"
          >
            <UserRound aria-hidden="true" className="h-5 w-5" />
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <div
      className={
        isMobile
          ? "flex flex-col gap-2 rounded-2xl bg-card p-3"
          : "flex items-center gap-3 rounded-2xl bg-card p-3"
      }
    >
      {/* On mobile this wrapper is a real flex row: avatar + name/reason are
          row 1, and `actions` renders separately below as the outer
          flex-col's row 2. On desktop it is `display: contents` instead, so
          it drops out of the box tree and the avatar, name/reason link and
          actions remain direct children of the single flex row -- the
          original, unchanged desktop layout. */}
      <div className={isMobile ? "flex items-center gap-3" : "contents"}>
        <span
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold"
        >
          {card.initials}
        </span>
        {href ? (
          <Link to={href} className="min-w-0 flex-1">
            {NameAndReason}
          </Link>
        ) : (
          NameAndReason
        )}
        {!isMobile ? actions : null}
      </div>
      {isMobile ? actions : null}
      {canLogActivity ? (
        <LogActivityForm
          dealId={card.dealId!}
          open={activityLogOpen}
          onOpenChange={(open) => {
            setActivityLogOpen(open);
            // The alert-augmented reason (`alert` above) only clears once
            // `lead_alerts` is re-evaluated (acceptance criterion 3) -- the
            // form's own query invalidation covers `tasks`/`deal_notes`, not
            // this hook's separately-fetched alert list.
            if (!open && alert) onAlertActivityLogged?.();
          }}
        />
      ) : null}
      {canOpenProfile ? (
        <DealProfileSheet
          dealId={card.dealId!}
          leadName={card.name}
          open={profileSheetOpen}
          onOpenChange={setProfileSheetOpen}
        />
      ) : null}
    </div>
  );
};
